/**
 * Attendance Parser Utilities
 *
 * Parses and formats HMC event attendance data from special_event field.
 * Cleans up _x000D_ artifacts and extracts structured attendance information.
 *
 * Format examples:
 * - "Hanh (8), Bobby (4), Katie (4)"
 * - "Scroggins: 6, Alan Brown (4) Lakeview Glass"
 * - "PC\nBrent McLennan: 6\nEB\nScroggins: 4"
 */

/**
 * NBA Team Name Mappings
 *
 * Complete list of all 30 NBA teams with their common abbreviations,
 * nicknames, and city variations. Used for expanding team names in
 * event titles for proper sports API integration.
 *
 * Format: short name -> full official name
 */
export const NBA_TEAM_NAMES: Record<string, string> = {
  // Atlantic Division
  'celtics': 'Boston Celtics',
  'boston': 'Boston Celtics',
  'nets': 'Brooklyn Nets',
  'brooklyn': 'Brooklyn Nets',
  'knicks': 'New York Knicks',
  'new york': 'New York Knicks',
  '76ers': 'Philadelphia 76ers',
  'sixers': 'Philadelphia 76ers',
  'philadelphia': 'Philadelphia 76ers',
  'philly': 'Philadelphia 76ers',
  'raptors': 'Toronto Raptors',
  'toronto': 'Toronto Raptors',

  // Central Division
  'bulls': 'Chicago Bulls',
  'chicago': 'Chicago Bulls',
  'cavaliers': 'Cleveland Cavaliers',
  'cavs': 'Cleveland Cavaliers',
  'cleveland': 'Cleveland Cavaliers',
  'pistons': 'Detroit Pistons',
  'detroit': 'Detroit Pistons',
  'pacers': 'Indiana Pacers',
  'indiana': 'Indiana Pacers',
  'bucks': 'Milwaukee Bucks',
  'milwaukee': 'Milwaukee Bucks',

  // Southeast Division
  'hawks': 'Atlanta Hawks',
  'atlanta': 'Atlanta Hawks',
  'hornets': 'Charlotte Hornets',
  'charlotte': 'Charlotte Hornets',
  'heat': 'Miami Heat',
  'miami': 'Miami Heat',
  'magic': 'Orlando Magic',
  'orlando': 'Orlando Magic',
  'wizards': 'Washington Wizards',
  'washington': 'Washington Wizards',

  // Northwest Division
  'nuggets': 'Denver Nuggets',
  'denver': 'Denver Nuggets',
  'timberwolves': 'Minnesota Timberwolves',
  'wolves': 'Minnesota Timberwolves',
  'minnesota': 'Minnesota Timberwolves',
  'thunder': 'Oklahoma City Thunder',
  'okc': 'Oklahoma City Thunder',
  'oklahoma': 'Oklahoma City Thunder',
  'trail blazers': 'Portland Trail Blazers',
  'blazers': 'Portland Trail Blazers',
  'portland': 'Portland Trail Blazers',
  'jazz': 'Utah Jazz',
  'utah': 'Utah Jazz',

  // Pacific Division
  'warriors': 'Golden State Warriors',
  'golden state': 'Golden State Warriors',
  'gsw': 'Golden State Warriors',
  'clippers': 'LA Clippers',
  'la clippers': 'LA Clippers',
  'lakers': 'Los Angeles Lakers',
  'la lakers': 'Los Angeles Lakers',
  'los angeles lakers': 'Los Angeles Lakers',
  'suns': 'Phoenix Suns',
  'phoenix': 'Phoenix Suns',
  'kings': 'Sacramento Kings',
  'sacramento': 'Sacramento Kings',

  // Southwest Division
  'mavericks': 'Dallas Mavericks',
  'mavs': 'Dallas Mavericks',
  'dallas': 'Dallas Mavericks',
  'rockets': 'Houston Rockets',
  'houston': 'Houston Rockets',
  'grizzlies': 'Memphis Grizzlies',
  'memphis': 'Memphis Grizzlies',
  'pelicans': 'New Orleans Pelicans',
  'new orleans': 'New Orleans Pelicans',
  'nola': 'New Orleans Pelicans',
  'spurs': 'San Antonio Spurs',
  'san antonio': 'San Antonio Spurs',
};

/**
 * MLS Team Name Mappings (for Dynamo/Dash)
 */
export const MLS_TEAM_NAMES: Record<string, string> = {
  'dynamo': 'Houston Dynamo',
  'houston dynamo': 'Houston Dynamo',
  'dash': 'Houston Dash',
  'houston dash': 'Houston Dash',
  'austin fc': 'Austin FC',
  'austin': 'Austin FC',
  'fc dallas': 'FC Dallas',
  'dallas fc': 'FC Dallas',
  'inter miami': 'Inter Miami CF',
  'miami cf': 'Inter Miami CF',
  'la galaxy': 'LA Galaxy',
  'galaxy': 'LA Galaxy',
  'lafc': 'Los Angeles FC',
  'los angeles fc': 'Los Angeles FC',
  'sounders': 'Seattle Sounders FC',
  'seattle': 'Seattle Sounders FC',
  'timbers': 'Portland Timbers',
  'sporting kc': 'Sporting Kansas City',
  'sporting kansas city': 'Sporting Kansas City',
  'skc': 'Sporting Kansas City',
  'real salt lake': 'Real Salt Lake',
  'rsl': 'Real Salt Lake',
  'colorado rapids': 'Colorado Rapids',
  'rapids': 'Colorado Rapids',
  'minnesota united': 'Minnesota United FC',
  'atlanta united': 'Atlanta United FC',
  'nashville sc': 'Nashville SC',
  'charlotte fc': 'Charlotte FC',
  'new york red bulls': 'New York Red Bulls',
  'red bulls': 'New York Red Bulls',
  'nycfc': 'New York City FC',
  'new york city fc': 'New York City FC',
  'new england': 'New England Revolution',
  'revolution': 'New England Revolution',
  'columbus crew': 'Columbus Crew',
  'crew': 'Columbus Crew',
  'cf montreal': 'CF Montréal',
  'montreal': 'CF Montréal',
  'toronto fc': 'Toronto FC',
  'dc united': 'D.C. United',
  'san jose': 'San Jose Earthquakes',
  'earthquakes': 'San Jose Earthquakes',
  'quakes': 'San Jose Earthquakes',
  'vancouver': 'Vancouver Whitecaps FC',
  'whitecaps': 'Vancouver Whitecaps FC',
};

/**
 * Expand team name abbreviation to full official name
 *
 * @param teamName - Short team name or abbreviation (e.g., "Warriors", "New Orleans")
 * @returns Full official team name (e.g., "Golden State Warriors", "New Orleans Pelicans")
 */
export function expandTeamName(teamName: string | null | undefined): string {
  if (!teamName) return '';

  const normalized = teamName.toLowerCase().trim();

  // Check NBA teams first
  if (NBA_TEAM_NAMES[normalized]) {
    return NBA_TEAM_NAMES[normalized];
  }

  // Check MLS teams
  if (MLS_TEAM_NAMES[normalized]) {
    return MLS_TEAM_NAMES[normalized];
  }

  // Return original if no match found (might be a concert or other event)
  return teamName;
}

/**
 * Parse and expand sports event name with full team names
 *
 * Takes an event name like "Rockets v New Orleans" and expands it to
 * "Houston Rockets vs New Orleans Pelicans" for proper sports API lookup.
 *
 * @param eventName - The event name to parse
 * @returns Expanded event name with full team names
 */
export function expandSportsEventName(eventName: string | null | undefined): string {
  if (!eventName) return '';

  // First clean the event name (remove suffixes)
  const cleanName = getCleanEventName(eventName);
  if (!cleanName) return '';

  // Match patterns like "Team1 v Team2", "Team1 vs Team2", "Team1 vs. Team2"
  const vsPatterns = [
    /^(.+?)\s+v\.?\s+(.+)$/i,   // "v" or "v."
    /^(.+?)\s+vs\.?\s+(.+)$/i,  // "vs" or "vs."
    /^(.+?)\s+@\s+(.+)$/i,      // "at" style for away games
  ];

  for (const pattern of vsPatterns) {
    const match = cleanName.match(pattern);
    if (match) {
      const team1 = match[1].trim();
      const team2 = match[2].trim();

      const expandedTeam1 = expandTeamName(team1);
      const expandedTeam2 = expandTeamName(team2);

      return `${expandedTeam1} vs ${expandedTeam2}`;
    }
  }

  // No "vs" pattern found - just try to expand single team name
  return expandTeamName(cleanName) || cleanName;
}

/**
 * Get the opponent team name from an event, expanded to full name
 *
 * @param eventName - The full event name (e.g., "Rockets v New Orleans EB 7:00")
 * @returns The expanded opponent name (e.g., "New Orleans Pelicans")
 */
export function getExpandedOpponentName(eventName: string | null | undefined): string | null {
  if (!eventName) return null;

  const cleanName = getCleanEventName(eventName);
  if (!cleanName) return null;

  // Match patterns like "Team1 v Team2"
  const vsPatterns = [
    /^(.+?)\s+v\.?\s+(.+)$/i,
    /^(.+?)\s+vs\.?\s+(.+)$/i,
  ];

  for (const pattern of vsPatterns) {
    const match = cleanName.match(pattern);
    if (match) {
      const team2 = match[2].trim();
      return expandTeamName(team2);
    }
  }

  return null;
}

/**
 * Attendance-related patterns found at the END of event names
 * These should be stripped from display name and moved to attendance section
 *
 * Examples:
 * - "Rockets v Clippers EB 7:00 - (Scroggins)" -> "Rockets v Clippers"
 * - "Rockets v Celtics PC (HUBER) 6:30" -> "Rockets v Celtics"
 * - "Dynamo v Austin FC PC 7:00" -> "Dynamo v Austin FC"
 * - "Taylor Swift Concert - PC CARRIER NIGHT" -> "Taylor Swift Concert"
 * - "Fantasia EB 7:00" -> "Fantasia"
 */
const ATTENDANCE_SUFFIX_PATTERNS = [
  // GROUP A: Complex patterns (check first - more specific)
  // Pattern: "EB 7:00 - Bobby Anderson" (section + time + dash + person name)
  /\s+(?:EB|PC)\s+\d{1,2}[:\.]?\d{0,2}\s*[-–]\s*[A-Z][a-zA-Z\s]+$/i,
  // Pattern: "EB 7:00 - (Scroggins)" or "EB 7:00 (Scroggins)"
  /\s+(?:EB|PC)\s+\d{1,2}[:\.]?\d{0,2}\s*[-–]?\s*\([^)]+\)\s*$/i,
  // Pattern: "PC (HUBER) 6:30" or "EB (NAME) 7:00"
  /\s+(?:EB|PC)\s+\([^)]+\)\s*\d{1,2}[:\.]?\d{0,2}\s*$/i,
  // Pattern: "EB - 6:00" or "PC - 7:30" (section + dash + time)
  /\s+(?:EB|PC)\s*[-–]\s*\d{1,2}[:\.]?\d{0,2}\s*$/i,
  // Pattern: "EB 7:00" or "PC 6:30" (section + time at end)
  /\s+(?:EB|PC)\s+\d{1,2}[:\.]?\d{0,2}\s*(?:am|pm)?\s*$/i,
  // Pattern: "EB - Name" (section + dash + name, no time)
  /\s+(?:EB|PC)\s*[-–]\s*[A-Z][a-zA-Z\s]+$/i,

  // GROUP B: Time patterns
  // Pattern: time range "7:00 - 9:00" or "6:30-8:30pm"
  /\s+\d{1,2}[:\.]?\d{0,2}\s*[-–]\s*\d{1,2}[:\.]?\d{0,2}\s*(?:am|pm)?\s*$/i,
  // Pattern: time with AM/PM "7:00 PM"
  /\s+\d{1,2}[:\.]?\d{0,2}\s*(?:AM|PM|am|pm)\s*$/i,
  // Pattern: trailing time like "8:30" at end (e.g., "Rockets v LA Lakers 8:30")
  /\s+\d{1,2}:\d{2}\s*$/,

  // GROUP C: Parenthetical patterns
  // Pattern: "(NAME) 6:30" or "- (Scroggins)" at end
  /\s*[-–]?\s*\([A-Z][a-zA-Z\s]+\)\s*\d{0,2}[:\.]?\d{0,2}\s*$/,
  // Pattern: Numbers in parentheses "(5/20)" or "(10 out of 20)" or "(5)"
  /\s*\([0-9]+\s*(?:out\s+of|\/|capacity)?\s*[0-9]*\)\s*$/i,
  // Pattern: parentheses only at end "(SECTION)" or "(Notes)"
  /\s+\([^)]*\)\s*$/,
  // Pattern: brackets/quotes at end "[Name]" or "{INFO}"
  /\s+[\[\{][^\]\}]+[\]\}]\s*(?:\d{1,2}[:\.]?\d{0,2})?\s*$/i,

  // GROUP D: Keywords and special suffixes
  // Pattern: "PC CARRIER NIGHT" or "EB CARRIER NIGHT"
  /\s+(?:EB|PC)\s+CARRIER\s+NIGHT\s*$/i,
  // Pattern: "Carrier Night" at end (standalone)
  /\s+CARRIER\s+NIGHT\s*$/i,
  // Pattern: COMP, SUITE, VIP, etc.
  /\s+(?:COMP|COMPLIMENTARY|SUITE|VIP|RESERVED|PENDING|WAITLIST|STANDBY)\s*$/i,
  // Pattern: Company names at end "Lakeview Glass", "Cimarron Inc"
  /\s+[A-Z][a-zA-Z\s]*(?:Inc|LLC|Corp|Co\.?|Ltd|Company)\s*$/i,

  // GROUP E: Section-only and punctuation
  // Pattern: "EB" or "PC" with punctuation "Event EB," or "Game PC;"
  /\s+(?:EB|PC)[,;:.]?\s*$/i,
  // Pattern: "EB" or "PC" alone at end
  /\s+(?:EB|PC)\s*$/i,

  // GROUP F: Catch-all patterns (LAST - most aggressive)
  // Pattern: Dash + name at end "- Team notes" or "- Player info"
  /\s*[-–]\s*[A-Z][a-zA-Z\s]{2,}$/i,
];

export interface EventNameParts {
  cleanName: string;
  attendanceInfo: string | null;
}

/**
 * Parse event name and extract attendance-related suffixes
 *
 * @param eventName - The raw event name (opponent field)
 * @returns Object with clean display name and extracted attendance info
 */
export function parseEventName(eventName: string | null | undefined): EventNameParts {
  if (!eventName) {
    return { cleanName: '', attendanceInfo: null };
  }

  let cleanName = eventName.trim();
  let attendanceInfo: string | null = null;

  // Try each pattern to find and extract attendance suffix
  for (const pattern of ATTENDANCE_SUFFIX_PATTERNS) {
    const match = cleanName.match(pattern);
    if (match) {
      attendanceInfo = match[0].trim();
      cleanName = cleanName.replace(pattern, '').trim();
      // Continue checking for more patterns (some events have multiple)
    }
  }

  // Final cleanup: remove trailing hyphens/dashes
  cleanName = cleanName.replace(/\s*[-–—]\s*$/, '').trim();

  return { cleanName, attendanceInfo };
}

/**
 * Get clean event name for display (strips attendance suffixes)
 */
export function getCleanEventName(eventName: string | null | undefined): string {
  return parseEventName(eventName).cleanName;
}

export interface AttendeeEntry {
  name: string;
  tickets: number;
  company?: string;
  section?: 'PC' | 'EB'; // Price Club or Employee Benefits
}

export interface ParsedAttendance {
  entries: AttendeeEntry[];
  totalTickets: number;
  pcTickets: number; // Price Club total
  ebTickets: number; // Employee Benefits total
  rawText: string;
  cleanText: string;
}

/**
 * Clean the raw special_event text by removing _x000D_ artifacts
 * and normalizing whitespace
 */
export function cleanAttendanceText(raw: string | null | undefined): string {
  if (!raw) return '';

  return raw
    // Remove _x000D_ artifacts (carriage return encoding)
    .replace(/_x000D_/g, '\n')
    // Remove excessive whitespace
    .replace(/\n\s*\n/g, '\n')
    .replace(/^\s+|\s+$/gm, '')
    // Clean up multiple spaces
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Parse attendance entries from cleaned text
 *
 * Handles formats like:
 * - Name (N)
 * - Name: N
 * - Name (N) Company
 * - Name: N Company
 */
export function parseAttendance(raw: string | null | undefined): ParsedAttendance {
  const result: ParsedAttendance = {
    entries: [],
    totalTickets: 0,
    pcTickets: 0,
    ebTickets: 0,
    rawText: raw || '',
    cleanText: '',
  };

  if (!raw) return result;

  const cleanText = cleanAttendanceText(raw);
  result.cleanText = cleanText;

  // Track current section (PC or EB)
  let currentSection: 'PC' | 'EB' | undefined = undefined;

  // Split by newlines and commas
  const lines = cleanText.split(/[\n,]+/).map(l => l.trim()).filter(Boolean);

  for (const line of lines) {
    // Check for section markers
    if (/^PC$/i.test(line)) {
      currentSection = 'PC';
      continue;
    }
    if (/^EB$/i.test(line)) {
      currentSection = 'EB';
      continue;
    }

    // Try to extract name and ticket count
    // Pattern 1: Name (N) [optional company]
    // Pattern 2: Name: N [optional company]
    // Pattern 3: N for Company (e.g., "3 for Cimarron")

    // Pattern: N for Company
    const forMatch = line.match(/^(\d+)\s+for\s+(.+)$/i);
    if (forMatch) {
      const tickets = parseInt(forMatch[1], 10);
      const company = forMatch[2].trim();
      result.entries.push({
        name: company,
        tickets,
        section: currentSection,
      });
      result.totalTickets += tickets;
      if (currentSection === 'PC') result.pcTickets += tickets;
      if (currentSection === 'EB') result.ebTickets += tickets;
      continue;
    }

    // Pattern: Name (N) [Company] or Name: N [Company]
    const match = line.match(/^(.+?)(?:\s*\((\d+)\)|\s*:\s*(\d+))(?:\s+(.+))?$/);
    if (match) {
      const name = match[1].trim();
      const tickets = parseInt(match[2] || match[3], 10);
      const company = match[4]?.trim();

      // Skip if name is just a number or empty
      if (!name || /^\d+$/.test(name)) continue;

      result.entries.push({
        name,
        tickets,
        company,
        section: currentSection,
      });
      result.totalTickets += tickets;
      if (currentSection === 'PC') result.pcTickets += tickets;
      if (currentSection === 'EB') result.ebTickets += tickets;
      continue;
    }

    // Fallback: just a name with no count (treat as 0 tickets)
    // This handles lines like "Standby for 3 additional" or notes
    const standbyMatch = line.match(/standby.*?(\d+)/i);
    if (standbyMatch) {
      result.entries.push({
        name: 'Standby',
        tickets: parseInt(standbyMatch[1], 10),
        section: currentSection,
      });
      // Don't add standby to total - these are pending
      continue;
    }
  }

  return result;
}

/**
 * Format attendance for clean display
 * Returns formatted string with names and ticket counts
 */
export function formatAttendanceDisplay(raw: string | null | undefined): string {
  const parsed = parseAttendance(raw);

  if (parsed.entries.length === 0) {
    return parsed.cleanText || '';
  }

  // Group by section
  const pcEntries = parsed.entries.filter(e => e.section === 'PC');
  const ebEntries = parsed.entries.filter(e => e.section === 'EB');
  const otherEntries = parsed.entries.filter(e => !e.section);

  const lines: string[] = [];

  const formatEntry = (entry: AttendeeEntry): string => {
    const companyStr = entry.company ? ` (${entry.company})` : '';
    return `${entry.name}: ${entry.tickets}${companyStr}`;
  };

  if (pcEntries.length > 0) {
    lines.push('PC:');
    pcEntries.forEach(e => lines.push(`  ${formatEntry(e)}`));
  }

  if (ebEntries.length > 0) {
    lines.push('EB:');
    ebEntries.forEach(e => lines.push(`  ${formatEntry(e)}`));
  }

  if (otherEntries.length > 0) {
    otherEntries.forEach(e => lines.push(formatEntry(e)));
  }

  return lines.join('\n');
}

/**
 * Get a compact attendance summary for cards
 * Returns format like: "Hanh: 8, Bobby: 4, Katie: 4 (16 total)"
 */
export function getAttendanceSummary(raw: string | null | undefined, maxEntries = 3): string {
  const parsed = parseAttendance(raw);

  if (parsed.entries.length === 0) {
    return '';
  }

  const displayed = parsed.entries.slice(0, maxEntries);
  const remaining = parsed.entries.length - maxEntries;

  const parts = displayed.map(e => `${e.name}: ${e.tickets}`);

  if (remaining > 0) {
    parts.push(`+${remaining} more`);
  }

  return `${parts.join(', ')} (${parsed.totalTickets} total)`;
}

/**
 * Get just the total ticket count
 */
export function getTotalTickets(raw: string | null | undefined): number {
  return parseAttendance(raw).totalTickets;
}

/**
 * Check if event appears to be fully booked based on attendance data
 * This is a heuristic - events with attendance entries are considered "booked"
 */
export function isFullyBooked(raw: string | null | undefined, maxCapacity?: number): boolean {
  const parsed = parseAttendance(raw);

  // If max capacity is provided, check against it
  if (maxCapacity && parsed.totalTickets >= maxCapacity) {
    return true;
  }

  // Otherwise, can't determine fully booked status without capacity info
  return false;
}

/**
 * Toyota Center Event Capacity Settings
 *
 * Different ticket availability based on event type:
 * - Rockets games: 20 tickets available
 * - Concerts: 17 tickets available
 * - Dynamo/Dash games: 11 tickets available
 */
export interface EventCapacity {
  capacity: number;
  eventCategory: 'rockets' | 'concert' | 'dynamo' | 'dash' | 'other';
}

/**
 * Determine event capacity based on event name and type
 *
 * @param eventName - The event name/opponent field
 * @param eventType - The event_type field ('dynamo', 'dash', 'general')
 * @param venue - Optional venue name for additional context
 * @returns EventCapacity object with capacity and detected category
 */
export function getEventCapacity(
  eventName: string | null | undefined,
  eventType: string | null | undefined,
  venue?: string | null
): EventCapacity {
  const name = (eventName || '').toLowerCase();
  const type = (eventType || '').toLowerCase();
  const venueLower = (venue || '').toLowerCase();

  // Check for Rockets games (Toyota Center)
  const isRockets =
    name.includes('rockets') ||
    name.includes('houston rockets') ||
    (venueLower.includes('toyota center') && (
      name.includes(' v ') ||
      name.includes(' vs ') ||
      name.includes('nba')
    ));

  if (isRockets) {
    return { capacity: 20, eventCategory: 'rockets' };
  }

  // Check for Dynamo games
  const isDynamo =
    type === 'dynamo' ||
    name.includes('dynamo') ||
    name.includes('houston dynamo');

  if (isDynamo) {
    return { capacity: 11, eventCategory: 'dynamo' };
  }

  // Check for Dash games
  const isDash =
    type === 'dash' ||
    name.includes('dash') ||
    name.includes('houston dash');

  if (isDash) {
    return { capacity: 11, eventCategory: 'dash' };
  }

  // Check for concerts/entertainment (Toyota Center)
  // Any Toyota Center event that's not sports is likely a concert
  const isToyotaCenter = venueLower.includes('toyota center');
  const isConcert =
    name.includes('tour') ||
    name.includes('concert') ||
    name.includes('live') ||
    name.includes('show') ||
    (isToyotaCenter && !isRockets);

  if (isConcert || (type === 'general' && isToyotaCenter)) {
    return { capacity: 17, eventCategory: 'concert' };
  }

  // Default capacity for other events
  return { capacity: 20, eventCategory: 'other' };
}

/**
 * Common first names for detecting person names vs team names
 * Used to identify "assigned suite" events where a person is listed instead of opponent team
 */
const COMMON_FIRST_NAMES = new Set([
  // Male names
  'james', 'john', 'michael', 'david', 'robert', 'william', 'richard',
  'thomas', 'christopher', 'matthew', 'anthony', 'mark', 'donald',
  'steven', 'paul', 'andrew', 'joshua', 'kenneth', 'kevin', 'brian',
  'george', 'edward', 'ronald', 'timothy', 'jason', 'jeffrey', 'ryan',
  'jacob', 'gary', 'nicholas', 'eric', 'stephen', 'jonathan', 'larry',
  'justin', 'scott', 'brandon', 'benjamin', 'samuel', 'raymond',
  'gregory', 'frank', 'alexander', 'patrick', 'jack', 'henry', 'walter',
  'peter', 'carl', 'arthur', 'albert', 'joe', 'jim', 'bob', 'mike',
  'dan', 'dave', 'tom', 'chris', 'matt', 'tony', 'steve', 'bill',
  'bobby', 'alan', 'huber', 'scroggins', 'carrier',
  // Female names
  'mary', 'patricia', 'jennifer', 'linda', 'elizabeth', 'barbara',
  'susan', 'jessica', 'sarah', 'karen', 'nancy', 'lisa', 'betty',
  'margaret', 'sandra', 'ashley', 'dorothy', 'kimberly', 'emily',
  'donna', 'michelle', 'carol', 'amanda', 'melissa', 'deborah',
  'stephanie', 'rebecca', 'sharon', 'laura', 'cynthia', 'kathleen',
  'amy', 'angela', 'shirley', 'anna', 'brenda', 'pamela', 'emma',
  'nicole', 'helen', 'samantha', 'katherine', 'christine', 'debra',
  'katie', 'hanh',
]);

/**
 * Check if event name contains a person's name (indicating assigned suite)
 *
 * Assigned suite events have patterns like:
 * - "Rockets v Boston - EB 7:00 (Scroggins)"
 * - "Rockets v Sacramento EB 7:00 - (Alan Brown)"
 * - "Rockets v Golden State PC (HUBER) 6:30"
 * - "Rockets v New Orleans EB 7:00 - Bobby Anderson"
 * - "Rockets v LA Lakers 8:30 - Carrier Night"
 *
 * Unassigned events look like:
 * - "Rockets v Atlanta EB 7:00 PM" (just time + section, no name)
 * - "Rockets v Sacramento EB 7:00" (just time + section)
 */
function containsPersonName(eventName: string): boolean {
  const nameLower = eventName.toLowerCase();

  // Pattern 1: Name in parentheses after EB/PC and time
  // e.g., "EB 7:00 (Scroggins)" or "PC (HUBER) 6:30" or "- (Alan Brown)"
  const parenNameMatch = nameLower.match(/\(([^)]+)\)/);
  if (parenNameMatch) {
    const nameInParens = parenNameMatch[1].trim().toLowerCase();
    // Check if any word is a common first name or known assignee
    const words = nameInParens.split(/\s+/);
    for (const word of words) {
      if (COMMON_FIRST_NAMES.has(word)) {
        return true;
      }
    }
    // Also check if it looks like "First Last" pattern (2-3 capitalized words)
    if (words.length >= 2 && words.length <= 3) {
      // Person name pattern
      return true;
    }
  }

  // Pattern 2: Name after dash at end (not in parens)
  // e.g., "EB 7:00 - Bobby Anderson" or "8:30 - Carrier Night"
  const dashNameMatch = nameLower.match(/[-–]\s*([a-zA-Z][a-zA-Z\s]+)\s*$/);
  if (dashNameMatch) {
    const afterDash = dashNameMatch[1].trim().toLowerCase();
    // Exclude pure time patterns like "9:00" or "PM"
    if (/^\d+:\d+/.test(afterDash) || /^(am|pm)$/i.test(afterDash)) {
      return false;
    }
    // Check for "Night" suffix (Carrier Night, etc.)
    if (afterDash.includes('night')) {
      return true;
    }
    // Check if first word is a common first name
    const words = afterDash.split(/\s+/);
    if (words.length >= 1 && COMMON_FIRST_NAMES.has(words[0])) {
      return true;
    }
    // 2+ word name after dash is likely a person
    if (words.length >= 2) {
      return true;
    }
  }

  // Pattern 3: PC suffix with name (not just "PC" alone)
  // e.g., "Rockets v Warriors PC John Smith"
  const pcWithNameMatch = nameLower.match(/\bpc\s+([a-zA-Z][a-zA-Z\s]+)$/);
  if (pcWithNameMatch) {
    const afterPc = pcWithNameMatch[1].trim();
    // Check if it's a known first name
    const words = afterPc.split(/\s+/);
    if (COMMON_FIRST_NAMES.has(words[0])) {
      return true;
    }
  }

  return false;
}

/**
 * Check if an event is a sports event (contains "v" or "vs" pattern)
 */
export function isSportsEvent(eventName: string | null | undefined): boolean {
  if (!eventName) return false;
  const lower = eventName.toLowerCase();
  return / v /i.test(lower) || / vs /i.test(lower) || / vs\. /i.test(lower);
}

/**
 * Check if an event is an "Assigned Suite" event
 *
 * Assigned Suite events are sports events where the suite has been assigned
 * to a specific person/company (not available for general requests).
 *
 * Detection criteria:
 * 1. Must be a sports event (contains "v" or "vs")
 * 2. Has "PC" marker (Private Club / Pre-Committed) - ANY "PC" event is assigned
 * 3. OR has a person's name after EB/PC section marker (in parens or after dash)
 * 4. OR has a "Night" suffix (e.g., "Carrier Night")
 *
 * NOT assigned if:
 * - Just has EB + time (e.g., "Rockets v Atlanta EB 7:00 PM")
 * - Concert or non-sports event
 *
 * @param eventName - The event opponent/name field
 * @returns true if this is an assigned suite event
 */
export function isAssignedSuiteEvent(eventName: string | null | undefined): boolean {
  if (!eventName) return false;

  // Only applies to sports events
  if (!isSportsEvent(eventName)) {
    return false;
  }

  // Check if event has "PC" marker (Private Club / Pre-Committed suite)
  // Any event with "PC" is considered assigned and excluded from Select All
  const hasPCMarker = /\bPC\b/i.test(eventName);
  if (hasPCMarker) {
    return true;
  }

  // Check for person name patterns
  return containsPersonName(eventName);
}

/**
 * Detect section marker (EB or PC) from event name
 *
 * When an event name contains "EB" or "PC" as a section marker (e.g., "Rockets v Oklahoma City EB 7:00"),
 * it means ALL tickets for that event are assigned to that section (not split 50/50).
 *
 * Pattern detection:
 * - "Event Name EB 7:00" -> EB (all tickets go to EB)
 * - "Event Name PC 7:00" -> PC (all tickets go to PC)
 * - "Event Name EB - (Name)" -> EB
 * - "Event Name PC (HUBER) 6:30" -> PC
 *
 * @param eventName - The event name/opponent field
 * @returns 'EB' | 'PC' | null if no section marker found
 */
export function getEventNameSectionMarker(eventName: string | null | undefined): 'EB' | 'PC' | null {
  if (!eventName) return null;

  // Check for EB marker in the event name
  // Patterns: "EB 7:00", "EB - Name", "EB (Name)", standalone "EB" at end
  const ebPattern = /\bEB\b(?:\s+\d{1,2}[:\.]?\d{0,2}|\s*[-–]|\s*\(|\s*$)/i;
  if (ebPattern.test(eventName)) {
    return 'EB';
  }

  // Check for PC marker in the event name
  // Patterns: "PC 7:00", "PC - Name", "PC (Name)", standalone "PC" at end
  const pcPattern = /\bPC\b(?:\s+\d{1,2}[:\.]?\d{0,2}|\s*[-–]|\s*\(|\s*$)/i;
  if (pcPattern.test(eventName)) {
    return 'PC';
  }

  return null;
}

/**
 * Ticket capacity split between PC and EB
 *
 * All events have their total capacity split 50/50 between PC (Producer Circle)
 * and EB (Employee Benefits). Availability for guest requests is calculated
 * based on EB tickets remaining only.
 *
 * EXCEPTION: When the event NAME contains "EB" or "PC" marker (e.g., "Rockets v OKC EB 7:00"),
 * ALL tickets are assigned to that section instead of being split 50/50.
 */
export interface TicketCapacitySplit {
  totalCapacity: number;
  pcCapacity: number;
  ebCapacity: number;
  pcUsed: number;
  ebUsed: number;
  pcRemaining: number;
  ebRemaining: number;
  eventCategory: string;
  /** Whether all tickets are assigned to a single section based on event name marker */
  singleSectionAllocation?: 'EB' | 'PC' | null;
}

/**
 * Get ticket capacity split with 50/50 PC/EB allocation
 *
 * IMPORTANT: When the event NAME contains "EB" or "PC" marker (e.g., "Rockets v OKC EB 7:00"),
 * ALL tickets are assigned to that section. The total attendance count is used as the "used"
 * count for that section.
 *
 * @param specialEvent - The special_event field containing attendance data
 * @param eventName - Event name for capacity detection AND section marker detection
 * @param eventType - Event type for capacity detection
 * @param venue - Optional venue for additional context
 * @returns Detailed capacity split between PC and EB sections
 */
export function getTicketCapacitySplit(
  specialEvent: string | null | undefined,
  eventName: string | null | undefined,
  eventType: string | null | undefined,
  venue?: string | null
): TicketCapacitySplit {
  const attendance = parseAttendance(specialEvent);
  const { capacity: totalCapacity, eventCategory } = getEventCapacity(eventName, eventType, venue);

  // Check if event name has a section marker (EB or PC)
  // If so, ALL tickets are assigned to that section
  const sectionMarker = getEventNameSectionMarker(eventName);

  if (sectionMarker === 'EB') {
    // ALL tickets are EB - PC gets none
    return {
      totalCapacity,
      pcCapacity: 0,
      ebCapacity: totalCapacity,
      pcUsed: 0,
      ebUsed: attendance.totalTickets, // All attendance goes to EB
      pcRemaining: 0,
      ebRemaining: Math.max(0, totalCapacity - attendance.totalTickets),
      eventCategory,
      singleSectionAllocation: 'EB',
    };
  }

  if (sectionMarker === 'PC') {
    // ALL tickets are PC - EB gets none
    return {
      totalCapacity,
      pcCapacity: totalCapacity,
      ebCapacity: 0,
      pcUsed: attendance.totalTickets, // All attendance goes to PC
      ebUsed: 0,
      pcRemaining: Math.max(0, totalCapacity - attendance.totalTickets),
      ebRemaining: 0,
      eventCategory,
      singleSectionAllocation: 'PC',
    };
  }

  // Default: Split capacity 50/50 between PC and EB
  const pcCapacity = Math.floor(totalCapacity / 2);
  const ebCapacity = totalCapacity - pcCapacity; // EB gets the extra ticket if odd

  return {
    totalCapacity,
    pcCapacity,
    ebCapacity,
    pcUsed: attendance.pcTickets,
    ebUsed: attendance.ebTickets,
    pcRemaining: Math.max(0, pcCapacity - attendance.pcTickets),
    ebRemaining: Math.max(0, ebCapacity - attendance.ebTickets),
    eventCategory,
    singleSectionAllocation: null,
  };
}

/**
 * Get tickets remaining for an event
 *
 * @param specialEvent - The special_event field containing attendance data
 * @param eventName - Event name for capacity detection
 * @param eventType - Event type for capacity detection
 * @param venue - Optional venue for additional context
 * @returns Object with total tickets used, capacity, and remaining
 */
export function getTicketsRemaining(
  specialEvent: string | null | undefined,
  eventName: string | null | undefined,
  eventType: string | null | undefined,
  venue?: string | null
): { used: number; capacity: number; remaining: number; eventCategory: string } {
  const ticketsUsed = getTotalTickets(specialEvent);
  const { capacity, eventCategory } = getEventCapacity(eventName, eventType, venue);
  const remaining = Math.max(0, capacity - ticketsUsed);

  return {
    used: ticketsUsed,
    capacity,
    remaining,
    eventCategory,
  };
}
